/**
 * AdminDataCachePage — admin-only dashboard showing all cached market data.
 *
 * Displays summary cards (totals) and per-dataset tables for:
 * candles, funding rates, open interest, long/short ratio, and alt metrics.
 *
 * Access is restricted to users with role === 'admin'.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore.js';
import {
  getDataCacheStats,
  triggerCacheRefresh,
  getCacheRefreshStatus,
  getDataHealth,
  triggerDataRepair,
  triggerRepairAll,
  getRepairStatus,
  getCronSchedule,
  type DataCacheStats,
  type DataCacheCandleRow,
  type DataCacheAltMetricRow,
  type DataCacheLiquidationRow,
  type CacheJobStatus,
  type DataHealthReport,
  type CategoryHealth,
  type ConsistencyReport,
  type CronSchedule,
} from '../../api/client.js';

// Re-alias for local readability
type CandleRow = DataCacheCandleRow;
type AltMetricRow = DataCacheAltMetricRow;
type LiquidationRow = DataCacheLiquidationRow;

type TabId = 'candles' | 'funding' | 'oi' | 'lsr' | 'alt' | 'liquidations' | 'health';

type SortDir = 'asc' | 'desc';

type HealthStatus = 'ok' | 'warning' | 'error';

// ============================================================================
// Helpers
// ============================================================================

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) {
    return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  }
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStale(isoDate: string): boolean {
  return Date.now() - new Date(isoDate).getTime() > STALE_THRESHOLD_MS;
}

function formatLatestValue(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function formatTimeAgo(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

function statusColor(status: HealthStatus): string {
  if (status === 'ok') return 'text-green-400';
  if (status === 'warning') return 'text-yellow-400';
  return 'text-red-400';
}

function statusBgColor(status: HealthStatus): string {
  if (status === 'ok') return 'bg-green-400/20';
  if (status === 'warning') return 'bg-yellow-400/20';
  return 'bg-red-400/20';
}

function statusDot(status: HealthStatus): string {
  if (status === 'ok') return 'bg-green-400';
  if (status === 'warning') return 'bg-yellow-400';
  return 'bg-red-400';
}

function statusLabel(status: HealthStatus): string {
  if (status === 'ok') return 'Healthy';
  if (status === 'warning') return 'Warning';
  return 'Error';
}

// ============================================================================
// Sub-components
// ============================================================================

interface SummaryCardProps {
  label: string;
  value: string;
  healthStatus?: HealthStatus;
  missingItems?: number;
  gapCount?: number;
}

function SummaryCard({ label, value, healthStatus, missingItems, gapCount }: SummaryCardProps) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-gray-400 mt-1 flex items-center gap-1.5">
        {healthStatus !== undefined && (
          <span
            className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${statusDot(healthStatus)}`}
            title={statusLabel(healthStatus)}
          />
        )}
        <span>{label}</span>
      </div>
      {healthStatus !== undefined && healthStatus !== 'ok' && gapCount !== undefined && missingItems !== undefined && (
        <div className={`text-xs mt-1 ${statusColor(healthStatus)}`}>
          {gapCount} gap{gapCount !== 1 ? 's' : ''} ({formatNumber(missingItems)} missing)
        </div>
      )}
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  field: string;
  sortField: string;
  sortDir: SortDir;
  onSort: (field: string) => void;
}

function SortableHeader({ label, field, sortField, sortDir, onSort }: SortableHeaderProps) {
  const active = sortField === field;
  return (
    <th
      role="columnheader"
      className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white select-none whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <span className="text-gray-600">
          {active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
        </span>
      </span>
    </th>
  );
}

// ============================================================================
// Spinner
// ============================================================================

function Spinner({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ============================================================================
// Refresh Cache button
// ============================================================================

type RefreshStatus = 'idle' | 'running' | 'completed' | 'failed';

interface RefreshCacheButtonProps {
  onRefreshComplete: () => void;
}

function RefreshCacheButton({ onRefreshComplete }: RefreshCacheButtonProps) {
  const [status, setStatus] = useState<RefreshStatus>('idle');
  const [currentStep, setCurrentStep] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  async function handleRefresh() {
    if (status === 'running') return;

    setStatus('running');
    setCurrentStep('');
    setError('');

    let jobId: string;
    try {
      const result = await triggerCacheRefresh();
      jobId = result.jobId;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // 409 = already running
      if (msg.toLowerCase().includes('409') || msg.toLowerCase().includes('already')) {
        setError('Refresh already in progress');
      } else {
        setError(msg);
      }
      setStatus('failed');
      return;
    }

    pollRef.current = setInterval(async () => {
      let job: CacheJobStatus;
      try {
        job = await getCacheRefreshStatus(jobId);
      } catch {
        // Ignore transient polling errors — keep polling
        return;
      }

      if (job.currentStep !== undefined) {
        setCurrentStep(job.currentStep);
      }

      if (job.status !== 'running') {
        if (pollRef.current !== null) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setStatus(job.status);
        if (job.status === 'failed') {
          setError(job.errors.join(', ') || 'Refresh failed');
        } else {
          onRefreshComplete();
        }
      }
    }, 3000);
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        aria-label="Run cache update job"
        onClick={() => { void handleRefresh(); }}
        disabled={status === 'running'}
        className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 disabled:cursor-not-allowed border border-primary-500 disabled:border-primary-700 rounded-lg text-white transition-colors"
      >
        {status === 'running' ? (
          <>
            <Spinner />
            Refreshing...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh Cache
          </>
        )}
      </button>

      {status === 'running' && currentStep && (
        <span className="text-xs text-gray-400">{currentStep}</span>
      )}
      {status === 'completed' && (
        <span className="text-xs text-green-400">Cache refreshed successfully</span>
      )}
      {status === 'failed' && (
        <span className="text-xs text-red-400">{error}</span>
      )}
    </div>
  );
}

// ============================================================================
// Health Status Bar
// ============================================================================

interface HealthStatusBarProps {
  health: DataHealthReport | null;
  healthLoading: boolean;
  cronSchedule: CronSchedule | null;
  onCheckNow: () => void;
}

function HealthStatusBar({ health, healthLoading, cronSchedule, onCheckNow }: HealthStatusBarProps) {
  function getOverallLabel(report: DataHealthReport): string {
    if (report.overall === 'ok') return 'All Data Healthy';
    const categories = Object.values(report.categories) as CategoryHealth[];
    const errorCount = categories.filter((c) => c.status === 'error').length;
    const warningCount = categories.filter((c) => c.status === 'warning').length;
    if (report.overall === 'error') {
      return `${errorCount} Critical Issue${errorCount !== 1 ? 's' : ''}`;
    }
    return `${warningCount} Issue${warningCount !== 1 ? 's' : ''} Detected`;
  }

  function getNextRefreshLabel(): string {
    if (!cronSchedule) return '';
    const diffMs = new Date(cronSchedule.nextDailyRefresh).getTime() - Date.now();
    if (diffMs <= 0) return 'soon';
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 60) return `${diffMin}m`;
    return `${Math.floor(diffMin / 60)}h ${diffMin % 60}m`;
  }

  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
      health
        ? `${statusBgColor(health.overall)} border-${health.overall === 'ok' ? 'green' : health.overall === 'warning' ? 'yellow' : 'red'}-400/30`
        : 'bg-gray-800 border-gray-700'
    }`}>
      <div className="flex items-center gap-3">
        {healthLoading ? (
          <Spinner className="w-4 h-4 text-gray-400" />
        ) : health ? (
          <span className={`text-lg ${statusColor(health.overall)}`}>
            {health.overall === 'ok' ? '✓' : health.overall === 'warning' ? '⚠' : '✕'}
          </span>
        ) : (
          <span className="text-gray-500 text-lg">?</span>
        )}

        <div>
          <span className={`font-medium text-sm ${health ? statusColor(health.overall) : 'text-gray-400'}`}>
            {healthLoading
              ? 'Checking health...'
              : health
              ? getOverallLabel(health)
              : 'Health not checked yet'}
          </span>
          {health && (
            <span className="ml-2 text-xs text-gray-500">
              Last checked: {formatTimeAgo(health.checkedAt)}
            </span>
          )}
        </div>

        {cronSchedule && (
          <span className="text-xs text-gray-500 hidden sm:inline">
            Next refresh in {getNextRefreshLabel()}
          </span>
        )}
      </div>

      <button
        onClick={onCheckNow}
        disabled={healthLoading}
        className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-600 rounded text-gray-200 transition-colors"
      >
        {healthLoading ? 'Checking...' : 'Check Now'}
      </button>
    </div>
  );
}

// ============================================================================
// Repair Status
// ============================================================================

interface RepairStatusBadgeProps {
  running: boolean;
  currentStep?: string;
  onComplete: () => void;
}

function RepairStatusBadge({ running, currentStep, onComplete }: RepairStatusBadgeProps) {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [done, setDone] = useState<'success' | 'failed' | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!running) return;

    setDone(null);
    pollRef.current = setInterval(async () => {
      try {
        const result = await getRepairStatus();
        if (!result.running) {
          if (pollRef.current !== null) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          if (result.job?.status === 'completed') {
            setDone('success');
            onComplete();
          } else {
            setDone('failed');
            setErrorMsg(result.job?.errors.join(', ') ?? 'Repair failed');
          }
        }
      } catch {
        // ignore transient errors
      }
    }, 3000);

    return () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [running, onComplete]);

  if (!running && done === null) return null;

  return (
    <div className="flex items-center gap-2 text-sm">
      {running && (
        <>
          <Spinner className="w-4 h-4 text-blue-400" />
          <span className="text-blue-400">{currentStep ?? 'Repairing...'}</span>
        </>
      )}
      {done === 'success' && <span className="text-green-400">Repair completed</span>}
      {done === 'failed' && <span className="text-red-400">{errorMsg}</span>}
    </div>
  );
}

// ============================================================================
// Health Tab — per-category gap details + repair
// ============================================================================

interface RepairRowButtonProps {
  report: ConsistencyReport;
  onRepairStarted: (jobId: string) => void;
  globalDisabled?: boolean;
}

function RepairRowButton({ report, onRepairStarted, globalDisabled }: RepairRowButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleRepair() {
    setLoading(true);
    setError('');
    try {
      const result = await triggerDataRepair({
        type: report.type,
        exchange: report.exchange,
        symbol: report.symbol,
        timeframe: report.timeframe,
        source: report.source,
        metric: report.metric,
      });
      onRepairStarted(result.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => { void handleRepair(); }}
        disabled={loading || globalDisabled}
        className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-50 border border-gray-600 rounded text-gray-200 transition-colors"
      >
        {loading ? 'Repairing...' : 'Repair'}
      </button>
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}

interface CategoryHealthSectionProps {
  name: string;
  categoryHealth: CategoryHealth;
  onRepairStarted: (jobId: string) => void;
  onRepairAll: () => void;
  repairRunning: boolean;
}

function CategoryHealthSection({ name, categoryHealth, onRepairStarted, onRepairAll, repairRunning }: CategoryHealthSectionProps) {
  const [expanded, setExpanded] = useState(categoryHealth.status !== 'ok');
  const [repairAllLoading, setRepairAllLoading] = useState(false);

  const gapDatasets = categoryHealth.details.filter((d) => d.status !== 'ok');

  async function handleRepairAll() {
    setRepairAllLoading(true);
    try {
      await onRepairAll();
    } finally {
      setRepairAllLoading(false);
    }
  }

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-3">
          <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusDot(categoryHealth.status)}`} />
          <span className="font-medium text-white text-sm">{name}</span>
          <span className="text-xs text-gray-500">
            {categoryHealth.healthyDatasets}/{categoryHealth.totalDatasets} healthy
            {categoryHealth.datasetsWithGaps > 0 && (
              <span className={`ml-2 ${statusColor(categoryHealth.status)}`}>
                {categoryHealth.datasetsWithGaps} with gaps ({formatNumber(categoryHealth.totalMissingItems)} missing)
              </span>
            )}
          </span>
        </div>
        <span className="text-gray-500 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="p-4 bg-gray-900 space-y-3">
          {gapDatasets.length === 0 ? (
            <p className="text-sm text-green-400">No gaps detected in this category.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{gapDatasets.length} dataset{gapDatasets.length !== 1 ? 's' : ''} with issues</span>
                <button
                  onClick={() => { void handleRepairAll(); }}
                  disabled={repairAllLoading || repairRunning}
                  className="px-3 py-1.5 text-xs bg-yellow-600/30 hover:bg-yellow-600/50 disabled:opacity-50 border border-yellow-600/50 rounded text-yellow-300 transition-colors"
                >
                  {repairAllLoading ? 'Repairing...' : `Repair All in ${name}`}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-gray-700">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-medium">Dataset</th>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-medium">Status</th>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-medium">Gaps</th>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-medium">Missing</th>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-medium">Gap Ranges</th>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gapDatasets.map((report, i) => {
                      const datasetLabel = [report.symbol, report.timeframe, report.source, report.metric]
                        .filter(Boolean)
                        .join(' / ');
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-gray-800/50' : ''}>
                          <td className="px-2 py-2 text-gray-200">{datasetLabel}</td>
                          <td className="px-2 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${statusBgColor(report.status)} ${statusColor(report.status)}`}>
                              {statusLabel(report.status)}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-gray-300 tabular-nums">{report.gaps.length}</td>
                          <td className="px-2 py-2 tabular-nums">
                            <span className={statusColor(report.status)}>
                              {formatNumber(report.missingCount)}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-gray-400 max-w-xs truncate">
                            {report.gaps.slice(0, 3).map((g, gi) => (
                              <span key={gi} className="inline-block mr-2">
                                {formatTimestamp(g.gapAfterTimestamp)} – {formatTimestamp(g.gapBeforeTimestamp)}
                                <span className="text-gray-500"> ({g.missingCount})</span>
                              </span>
                            ))}
                            {report.gaps.length > 3 && (
                              <span className="text-gray-500">+{report.gaps.length - 3} more</span>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <RepairRowButton report={report} onRepairStarted={onRepairStarted} globalDisabled={repairRunning} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface HealthTabProps {
  health: DataHealthReport | null;
  healthLoading: boolean;
  onHealthRefresh: () => void;
}

function HealthTab({ health, healthLoading, onHealthRefresh }: HealthTabProps) {
  const [repairRunning, setRepairRunning] = useState(false);
  const [repairStep, setRepairStep] = useState('');
  const [repairAllLoading, setRepairAllLoading] = useState(false);

  function handleRepairStarted(_jobId: string) {
    setRepairRunning(true);
    setRepairStep('Repair job started...');
  }

  function handleRepairComplete() {
    setRepairRunning(false);
    onHealthRefresh();
  }

  async function handleRepairAll() {
    setRepairAllLoading(true);
    try {
      await triggerRepairAll();
      setRepairRunning(true);
      setRepairStep('Full repair started...');
    } catch (err) {
      console.error('Repair all failed:', err);
    } finally {
      setRepairAllLoading(false);
    }
  }

  if (healthLoading && !health) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 gap-3">
        <Spinner className="w-5 h-5" />
        <span>Running health check...</span>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500 gap-3">
        <span className="text-4xl">?</span>
        <p className="text-sm">No health data available. Click "Check Now" to run a health check.</p>
      </div>
    );
  }

  const categoryEntries: Array<{ key: string; label: string; data: CategoryHealth }> = [
    { key: 'candles', label: 'Candles', data: health.categories.candles },
    { key: 'funding', label: 'Funding Rates', data: health.categories.funding },
    { key: 'oi', label: 'Open Interest', data: health.categories.oi },
    { key: 'lsr', label: 'Long/Short Ratio', data: health.categories.lsr },
    { key: 'liquidations', label: 'Liquidations', data: health.categories.liquidations },
    { key: 'altMetrics', label: 'Alt Metrics', data: health.categories.altMetrics },
  ];

  const hasIssues = health.overall !== 'ok';

  return (
    <div className="space-y-4">
      {/* Repair all + status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {hasIssues && (
            <button
              onClick={() => { void handleRepairAll(); }}
              disabled={repairAllLoading || repairRunning}
              className="px-4 py-2 text-sm bg-red-600/30 hover:bg-red-600/50 disabled:opacity-50 border border-red-600/50 rounded-lg text-red-300 transition-colors"
            >
              {repairAllLoading ? 'Starting...' : 'Repair All'}
            </button>
          )}
          <RepairStatusBadge
            running={repairRunning}
            currentStep={repairStep}
            onComplete={handleRepairComplete}
          />
        </div>
        {healthLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Spinner className="w-3 h-3" />
            <span>Refreshing...</span>
          </div>
        )}
      </div>

      {/* Per-category sections */}
      {categoryEntries.map(({ key, label, data }) => (
        <CategoryHealthSection
          key={key}
          name={label}
          categoryHealth={data}
          onRepairStarted={handleRepairStarted}
          onRepairAll={() => handleRepairAll()}
          repairRunning={repairRunning}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Cron Schedule Section
// ============================================================================

interface CronScheduleSectionProps {
  schedule: CronSchedule | null;
}

function CronScheduleSection({ schedule }: CronScheduleSectionProps) {
  if (!schedule) return null;

  return (
    <div className="flex flex-wrap gap-4 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-xs text-gray-400">
      <div>
        <span className="text-gray-500">Incremental refresh:</span>{' '}
        <span className="text-gray-300">every {schedule.incrementalRefreshIntervalMin} min</span>
      </div>
      <div>
        <span className="text-gray-500">Consistency check:</span>{' '}
        <span className="text-gray-300">every {schedule.consistencyCheckIntervalMin} min</span>
      </div>
      <div>
        <span className="text-gray-500">Full refresh:</span>{' '}
        <span className="text-gray-300">daily at {String(schedule.dailyFullRefreshUtcHour).padStart(2, '0')}:00 UTC</span>
      </div>
      <div>
        <span className="text-gray-500">Next daily refresh:</span>{' '}
        <span className="text-gray-300">{new Date(schedule.nextDailyRefresh).toUTCString()}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Candles tab
// ============================================================================

interface CandlesTabProps {
  rows: CandleRow[];
}

function CandlesTab({ rows }: CandlesTabProps) {
  const [search, setSearch] = useState('');
  const [tfFilter, setTfFilter] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const timeframes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.timeframe))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    let result = rows;
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      result = result.filter((r) => r.symbol.toUpperCase().includes(q));
    }
    if (tfFilter) {
      result = result.filter((r) => r.timeframe === tfFilter);
    }
    return [...result].sort((a, b) => {
      const av = a[sortField as keyof CandleRow];
      const bv = b[sortField as keyof CandleRow];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, search, tfFilter, sortField, sortDir]);

  function handleSort(field: string) {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search symbol..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:border-primary-500"
        />
        <select
          value={tfFilter}
          onChange={(e) => setTfFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-500"
        >
          <option value="">All timeframes</option>
          {timeframes.map((tf) => (
            <option key={tf} value={tf}>
              {tf}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-500">{filtered.length} rows</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-900 border-b border-gray-700">
            <tr>
              <SortableHeader label="Symbol" field="symbol" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="TF" field="timeframe" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Count" field="count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="From" field="earliest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="To" field="latest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Exchange</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr
                key={`${row.exchange}-${row.symbol}-${row.timeframe}`}
                className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}
              >
                <td className="px-3 py-2 text-gray-100">{row.symbol}</td>
                <td className="px-3 py-2 text-gray-300">{row.timeframe}</td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{formatNumber(row.count)}</td>
                <td className="px-3 py-2 text-gray-400 tabular-nums">{formatDate(row.earliest)}</td>
                <td className={`px-3 py-2 tabular-nums ${isStale(row.latest) ? 'text-yellow-400' : 'text-gray-400'}`}>
                  {formatDate(row.latest)}
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs">{row.exchange}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                  No results
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Generic market data tab (Funding / OI / LSR)
// ============================================================================

interface MarketDataRow {
  exchange: string;
  symbol: string;
  count: number;
  earliest: string;
  latest: string;
}

interface MarketDataTabProps {
  rows: MarketDataRow[];
}

function MarketDataTab({ rows }: MarketDataTabProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filtered = useMemo(() => {
    let result = rows;
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      result = result.filter((r) => r.symbol.toUpperCase().includes(q));
    }
    return [...result].sort((a, b) => {
      const av = a[sortField as keyof MarketDataRow];
      const bv = b[sortField as keyof MarketDataRow];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, search, sortField, sortDir]);

  function handleSort(field: string) {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search symbol..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:border-primary-500"
        />
        <span className="text-xs text-gray-500">{filtered.length} rows</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-900 border-b border-gray-700">
            <tr>
              <SortableHeader label="Symbol" field="symbol" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Count" field="count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="From" field="earliest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="To" field="latest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Exchange</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr
                key={`${row.exchange}-${row.symbol}`}
                className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}
              >
                <td className="px-3 py-2 text-gray-100">{row.symbol}</td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{formatNumber(row.count)}</td>
                <td className="px-3 py-2 text-gray-400 tabular-nums">{formatDate(row.earliest)}</td>
                <td className={`px-3 py-2 tabular-nums ${isStale(row.latest) ? 'text-yellow-400' : 'text-gray-400'}`}>
                  {formatDate(row.latest)}
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs">{row.exchange}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  No results
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Alt Metrics tab
// ============================================================================

interface AltMetricsTabProps {
  rows: AltMetricRow[];
}

function AltMetricsTab({ rows }: AltMetricsTabProps) {
  const [sortField, setSortField] = useState('source');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const av = a[sortField as keyof AltMetricRow];
        const bv = b[sortField as keyof AltMetricRow];
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
      }),
    [rows, sortField, sortDir]
  );

  function handleSort(field: string) {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-900 border-b border-gray-700">
          <tr>
            <SortableHeader label="Source" field="source" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <SortableHeader label="Metric" field="metric" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <SortableHeader label="Count" field="count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <SortableHeader label="From" field="earliest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <SortableHeader label="To" field="latest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <SortableHeader label="Latest Value" field="latestValue" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={`${row.source}-${row.metric}`}
              className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}
            >
              <td className="px-3 py-2 text-gray-100">{row.source}</td>
              <td className="px-3 py-2 text-gray-300">{row.metric}</td>
              <td className="px-3 py-2 text-gray-300 tabular-nums">{formatNumber(row.count)}</td>
              <td className="px-3 py-2 text-gray-400 tabular-nums">{formatDate(row.earliest)}</td>
              <td className={`px-3 py-2 tabular-nums ${isStale(row.latest) ? 'text-yellow-400' : 'text-gray-400'}`}>
                {formatDate(row.latest)}
              </td>
              <td className="px-3 py-2 text-gray-300 tabular-nums">{formatLatestValue(row.latestValue)}</td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                No results
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Liquidations tab
// ============================================================================

interface LiquidationsTabProps {
  rows: LiquidationRow[];
}

function LiquidationsTab({ rows }: LiquidationsTabProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filtered = useMemo(() => {
    let result = rows;
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      result = result.filter((r) => r.symbol.toUpperCase().includes(q));
    }
    return [...result].sort((a, b) => {
      const av = a[sortField as keyof LiquidationRow];
      const bv = b[sortField as keyof LiquidationRow];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, search, sortField, sortDir]);

  function handleSort(field: string) {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search symbol..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:border-primary-500"
        />
        <span className="text-xs text-gray-500">{filtered.length} rows</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-900 border-b border-gray-700">
            <tr>
              <SortableHeader label="Source" field="source" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Symbol" field="symbol" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Count" field="count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="From" field="earliest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="To" field="latest" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr
                key={`${row.source}-${row.symbol}`}
                className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}
              >
                <td className="px-3 py-2 text-gray-100">{row.source}</td>
                <td className="px-3 py-2 text-gray-300">{row.symbol}</td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{formatNumber(row.count)}</td>
                <td className="px-3 py-2 text-gray-400 tabular-nums">{formatDate(row.earliest)}</td>
                <td className={`px-3 py-2 tabular-nums ${isStale(row.latest) ? 'text-yellow-400' : 'text-gray-400'}`}>
                  {formatDate(row.latest)}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  No results
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export function AdminDataCachePage() {
  const user = useAuthStore((s) => s.user);

  const [stats, setStats] = useState<DataCacheStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('candles');

  // Health state
  const [health, setHealth] = useState<DataHealthReport | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Cron schedule state
  const [cronSchedule, setCronSchedule] = useState<CronSchedule | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDataCacheStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHealth = useCallback(async (useCached = false) => {
    setHealthLoading(true);
    try {
      const data = await getDataHealth(useCached);
      setHealth(data);
    } catch {
      // Health check failure is non-critical — leave health as null
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const fetchCronSchedule = useCallback(async () => {
    try {
      const schedule = await getCronSchedule();
      setCronSchedule(schedule);
    } catch {
      // Non-critical — cron schedule display is optional
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchStats();
      // Try cached health on initial load for fast display
      fetchHealth(true);
      fetchCronSchedule();
    }
  }, [user, fetchStats, fetchHealth, fetchCronSchedule]);

  // ---- Access denied ----
  if (!user || user.role !== 'admin') {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <p className="text-gray-300 font-medium">Access denied</p>
          <p className="text-gray-500 text-sm mt-1">
            This page is only accessible to administrators.
          </p>
        </div>
      </div>
    );
  }

  // ---- Loading ----
  if (loading && !stats) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="flex items-center gap-3 text-gray-400">
          <Spinner className="w-5 h-5" />
          <span>Loading data cache stats...</span>
        </div>
      </div>
    );
  }

  // ---- Error ----
  if (error && !stats) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <p className="text-red-400 font-medium">Failed to load data cache</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <button
            onClick={() => { void fetchStats(); }}
            className="mt-4 px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Build health lookup by category key
  const healthByCategory: Record<string, CategoryHealth | undefined> = health
    ? {
        candles: health.categories.candles,
        funding: health.categories.funding,
        oi: health.categories.oi,
        lsr: health.categories.lsr,
        alt: health.categories.altMetrics,
        liquidations: health.categories.liquidations,
      }
    : {};

  const tabs: { id: TabId; label: string; count: number; healthStatus?: HealthStatus }[] = stats
    ? [
        { id: 'candles', label: 'Candles', count: stats.candles?.length ?? 0, healthStatus: healthByCategory['candles']?.status },
        { id: 'funding', label: 'Funding', count: stats.fundingRates?.length ?? 0, healthStatus: healthByCategory['funding']?.status },
        { id: 'oi', label: 'OI', count: stats.openInterest?.length ?? 0, healthStatus: healthByCategory['oi']?.status },
        { id: 'lsr', label: 'LSR', count: stats.longShortRatio?.length ?? 0, healthStatus: healthByCategory['lsr']?.status },
        { id: 'alt', label: 'Alt', count: stats.altMetrics?.length ?? 0, healthStatus: healthByCategory['alt']?.status },
        { id: 'liquidations', label: 'Liquidations', count: stats.liquidations?.length ?? 0, healthStatus: healthByCategory['liquidations']?.status },
        { id: 'health', label: 'Health', count: 0 },
      ]
    : [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-white">Data Cache Dashboard</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Cached market data in the system database
          </p>
        </div>
        <div className="flex items-start gap-3">
          <RefreshCacheButton onRefreshComplete={fetchStats} />
          <button
            aria-label="Refresh stats"
            onClick={() => { void fetchStats(); }}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-gray-200 transition-colors disabled:opacity-50"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Reload
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {stats && (
          <div className="px-6 py-4 space-y-6">
            {/* Health Status Bar */}
            <HealthStatusBar
              health={health}
              healthLoading={healthLoading}
              cronSchedule={cronSchedule}
              onCheckNow={() => { void fetchHealth(false); }}
            />

            {/* Summary cards */}
            <section>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard
                  label="Candles"
                  value={formatNumber(stats.summary.totalCandles)}
                  healthStatus={healthByCategory['candles']?.status}
                  gapCount={healthByCategory['candles']?.datasetsWithGaps}
                  missingItems={healthByCategory['candles']?.totalMissingItems}
                />
                <SummaryCard
                  label="Funding Rates"
                  value={formatNumber(stats.summary.totalFundingRates)}
                  healthStatus={healthByCategory['funding']?.status}
                  gapCount={healthByCategory['funding']?.datasetsWithGaps}
                  missingItems={healthByCategory['funding']?.totalMissingItems}
                />
                <SummaryCard
                  label="Open Interest"
                  value={formatNumber(stats.summary.totalOpenInterest)}
                  healthStatus={healthByCategory['oi']?.status}
                  gapCount={healthByCategory['oi']?.datasetsWithGaps}
                  missingItems={healthByCategory['oi']?.totalMissingItems}
                />
                <SummaryCard
                  label="L/S Ratio"
                  value={formatNumber(stats.summary.totalLongShortRatio)}
                  healthStatus={healthByCategory['lsr']?.status}
                  gapCount={healthByCategory['lsr']?.datasetsWithGaps}
                  missingItems={healthByCategory['lsr']?.totalMissingItems}
                />
                <SummaryCard
                  label="Alt Metrics"
                  value={formatNumber(stats.summary.totalAltMetrics)}
                  healthStatus={healthByCategory['alt']?.status}
                  gapCount={healthByCategory['alt']?.datasetsWithGaps}
                  missingItems={healthByCategory['alt']?.totalMissingItems}
                />
                <SummaryCard
                  label="Liquidations"
                  value={formatNumber(stats.summary.totalLiquidations)}
                  healthStatus={healthByCategory['liquidations']?.status}
                  gapCount={healthByCategory['liquidations']?.datasetsWithGaps}
                  missingItems={healthByCategory['liquidations']?.totalMissingItems}
                />
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-white">{formatBytes(stats.summary.dbSizeBytes)}</div>
                  <div className="text-sm text-gray-400 mt-1">DB Size</div>
                </div>
              </div>
            </section>

            {/* Tabs */}
            <section>
              <div className="border-b border-gray-700 mb-4">
                <div className="flex gap-1 flex-wrap">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-400'
                          : 'border-transparent text-gray-400 hover:text-white hover:border-gray-500'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        {tab.healthStatus !== undefined && tab.id !== 'health' && (
                          <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot(tab.healthStatus)}`} />
                        )}
                        {tab.label}
                        {tab.id !== 'health' && (
                          <span className="text-xs text-gray-500">({tab.count})</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab content */}
              <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                <div className="p-4">
                  {activeTab === 'candles' && <CandlesTab rows={stats.candles} />}
                  {activeTab === 'funding' && <MarketDataTab rows={stats.fundingRates} />}
                  {activeTab === 'oi' && <MarketDataTab rows={stats.openInterest} />}
                  {activeTab === 'lsr' && <MarketDataTab rows={stats.longShortRatio} />}
                  {activeTab === 'alt' && <AltMetricsTab rows={stats.altMetrics} />}
                  {activeTab === 'liquidations' && <LiquidationsTab rows={stats.liquidations} />}
                  {activeTab === 'health' && (
                    <HealthTab
                      health={health}
                      healthLoading={healthLoading}
                      onHealthRefresh={() => { void fetchHealth(false); }}
                    />
                  )}
                </div>
              </div>
            </section>

            {/* Cron Schedule Section */}
            <CronScheduleSection schedule={cronSchedule} />
          </div>
        )}
      </div>
    </div>
  );
}
